import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Sessao } from '../../auth';
import { baixarCsv } from '../../design/csv';
import { ErroDeArea, EstadoVazio } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  ROTULO_DA_SITUACAO,
  definirAdmissao,
  diaMes,
  hojeBrasilia,
  incluirAjusteDeFerias,
  lerFerias,
  removerAjusteDeFerias,
} from '../../escala';
import type { BlocoDeFerias, FeriasDaPessoaNaEscala, PeriodoDeFerias } from '../../escala';
import { CalendarioAnual } from './CalendarioAnual';
import { CalendarioDeFerias } from './CalendarioDeFerias';
import { Kpi } from './QuadroDeHorario';

/**
 * FÉRIAS (dono 24/10/2026): "o pessoal costuma pegar picado, 3, 5, 10 dias — precisa saber o saldo
 * e o restante de cada um, quanto já tirou e que dia tirou".
 *
 * Os dias tirados vêm do QUADRO (cada FE). Com a data de admissão, o sistema monta os períodos
 * aquisitivos (12 meses → 30 dias, menos pelas faltas sem atestado — CLT art. 130), desconta cada
 * dia do período mais antigo e diz até quando tirar. O que veio de antes do sistema (ou venda de
 * 10 dias) entra como ajuste.
 */

const dataBr = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
const textoDoBloco = (b: BlocoDeFerias) =>
  b.dias === 1
    ? `${diaMes(b.de)} (1 dia)`
    : `${diaMes(b.de)} a ${diaMes(b.ate)} (${String(b.dias)} dias)`;

function diasAte(dia: string, hoje: string): number {
  return Math.round(
    (Date.parse(`${dia}T12:00:00Z`) - Date.parse(`${hoje}T12:00:00Z`)) / 86_400_000,
  );
}

type Filtro = 'todos' | 'vencendo' | 'sem-admissao';

/** Férias: o calendário do ano (onde o Carlos lança) e os saldos por pessoa. */
export function AbaFerias({ sessao }: { sessao: Sessao }) {
  const [vista, setVista] = useState<'calendario' | 'linhas' | 'saldos'>('calendario');
  const [versao, setVersao] = useState(0);
  return (
    <div className="qh-corpo">
      <div className="qh-segmentos" role="radiogroup" aria-label="Férias">
        {(
          [
            ['calendario', 'Calendário do ano'],
            ['linhas', 'Por pessoa'],
            ['saldos', 'Saldos e dias tirados'],
          ] as const
        ).map(([id, txt]) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={vista === id}
            className={vista === id ? 'qh-seg qh-seg-on' : 'qh-seg'}
            onClick={() => setVista(id)}
          >
            {txt}
          </button>
        ))}
      </div>
      {vista === 'calendario' ? (
        <CalendarioAnual sessao={sessao} aoMudar={() => setVersao((v) => v + 1)} />
      ) : vista === 'linhas' ? (
        <CalendarioDeFerias sessao={sessao} aoMudar={() => setVersao((v) => v + 1)} />
      ) : (
        <SaldosDeFerias key={versao} sessao={sessao} />
      )}
    </div>
  );
}

function SaldosDeFerias({ sessao }: { sessao: Sessao }) {
  const [dados, setDados] = useState<{ hoje: string; pessoas: FeriasDaPessoaNaEscala[] } | null>(
    null,
  );
  const [erro, setErro] = useState(false);
  const [aberta, setAberta] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);

  const carregar = useCallback(() => {
    setErro(false);
    void lerFerias().then((r) => (r.ok ? setDados(r.dados) : setErro(true)));
  }, []);
  useEffect(carregar, [carregar]);

  const trocar = (p: FeriasDaPessoaNaEscala | null) => {
    if (!p) return carregar();
    setDados((d) =>
      d ? { ...d, pessoas: d.pessoas.map((x) => (x.pessoaId === p.pessoaId ? p : x)) } : d,
    );
  };

  const linhas = useMemo(() => {
    const l = dados?.pessoas ?? [];
    if (filtro === 'sem-admissao') return l.filter((p) => !p.admissao);
    if (filtro === 'vencendo')
      return l.filter((p) =>
        p.ferias?.periodos.some((x) => x.situacao === 'VENCENDO' || x.situacao === 'VENCIDO'),
      );
    return l;
  }, [dados, filtro]);

  if (erro) return <ErroDeArea oQue="as férias" aoTentar={carregar} />;
  if (!dados) return <div className="qh-carregando">Somando as férias…</div>;

  const todas = dados.pessoas;
  const semAdmissao = todas.filter((p) => !p.admissao).length;
  const vencendo = todas.filter((p) =>
    p.ferias?.periodos.some((x) => x.situacao === 'VENCENDO'),
  ).length;
  const vencidos = todas.filter((p) =>
    p.ferias?.periodos.some((x) => x.situacao === 'VENCIDO'),
  ).length;
  const saldoTotal = todas.reduce((s, p) => s + (p.ferias?.saldo ?? 0), 0);

  const exportar = () =>
    baixarCsv(`ferias-${dados.hoje}`, [
      [
        'Pessoa',
        'Admissão',
        'Saldo (dias)',
        'Tirar até',
        'Período',
        'Direito',
        'Usou',
        'Dias tirados',
        'Situação',
      ],
      ...todas.flatMap((p) =>
        p.ferias
          ? p.ferias.periodos.map((x) => [
              p.nome,
              dataBr(p.ferias?.admissao ?? ''),
              p.ferias?.saldo ?? 0,
              p.ferias?.proximoVencimento ? dataBr(p.ferias.proximoVencimento.limite) : '',
              `${dataBr(x.inicio)} a ${dataBr(x.fim)}`,
              x.direito,
              x.usados,
              x.blocos.map(textoDoBloco).join(' · '),
              ROTULO_DA_SITUACAO[x.situacao],
            ])
          : [
              [
                p.nome,
                '',
                '',
                '',
                '',
                '',
                '',
                p.tiradas.map(textoDoBloco).join(' · '),
                'Sem data de admissão',
              ],
            ],
      ),
    ]);

  return (
    <div className="qh-corpo">
      <div className="qh-kpis">
        <Kpi rotulo="Dias de férias a tirar (todos)" valor={saldoTotal} />
        <Kpi rotulo={`Vencem em até 60 dias`} valor={vencendo} destaque={vencendo > 0} />
        <Kpi rotulo="Já vencidas (pagar em dobro)" valor={vencidos} destaque={vencidos > 0} />
        <Kpi rotulo="Sem data de admissão" valor={semAdmissao} destaque={semAdmissao > 0} />
      </div>
      <div className="qh-barra">
        <div className="qh-segmentos" role="radiogroup" aria-label="Mostrar">
          {(
            [
              ['todos', 'Todos'],
              ['vencendo', 'Vencendo / vencidas'],
              ['sem-admissao', 'Sem admissão'],
            ] as const
          ).map(([id, txt]) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={filtro === id}
              className={filtro === id ? 'qh-seg qh-seg-on' : 'qh-seg'}
              onClick={() => setFiltro(id)}
            >
              {txt}
            </button>
          ))}
        </div>
        <button type="button" className="qh-btn qh-btn-sec" onClick={exportar}>
          <Icone nome="down" tamanho={15} />
          Baixar planilha
        </button>
      </div>
      {aviso && (
        <div
          className={`qh-aviso qh-aviso-${aviso.tom}`}
          role={aviso.tom === 'erro' ? 'alert' : 'status'}
        >
          {aviso.texto}
          <button type="button" onClick={() => setAviso(null)} aria-label="Fechar aviso">
            <Icone nome="x" tamanho={14} />
          </button>
        </div>
      )}
      {linhas.length === 0 ? (
        <EstadoVazio icone="clock" titulo="Ninguém aqui" texto="Nenhuma pessoa neste filtro." />
      ) : (
        <ul className="qh-ferias">
          {linhas.map((p) => (
            <LinhaDeFerias
              key={p.pessoaId}
              p={p}
              hoje={dados.hoje}
              aberta={aberta === p.pessoaId}
              aoAbrir={() => setAberta(aberta === p.pessoaId ? null : p.pessoaId)}
              sessao={sessao}
              aoMudar={(np, texto) => {
                trocar(np);
                setAviso({ tom: 'ok', texto });
              }}
              aoErro={(texto) => setAviso({ tom: 'erro', texto })}
            />
          ))}
        </ul>
      )}
      <p className="qh-mudo">
        Os dias tirados vêm do quadro (cada FE). Período aquisitivo de 12 meses a partir da
        admissão: 30 dias, menos pelas faltas sem atestado (CLT art. 130). Tirar até o fim dos 12
        meses seguintes; depois disso a CLT manda pagar em dobro.
      </p>
    </div>
  );
}

function selo(p: FeriasDaPessoaNaEscala, hoje: string): { tom: string; texto: string } {
  if (!p.ferias) return { tom: 'neutro', texto: 'Sem admissão' };
  if (p.ferias.periodos.some((x) => x.situacao === 'VENCIDO'))
    return { tom: 'erro', texto: 'Vencida' };
  const v = p.ferias.proximoVencimento;
  if (v && diasAte(v.limite, hoje) <= 60)
    return { tom: 'alerta', texto: `Vence ${diaMes(v.limite)}` };
  if (p.ferias.saldo > 0) return { tom: 'ok', texto: 'Pode tirar' };
  return { tom: 'neutro', texto: 'Em dia' };
}

function LinhaDeFerias({
  p,
  hoje,
  aberta,
  aoAbrir,
  sessao,
  aoMudar,
  aoErro,
}: {
  p: FeriasDaPessoaNaEscala;
  hoje: string;
  aberta: boolean;
  aoAbrir: () => void;
  sessao: Sessao;
  aoMudar: (p: FeriasDaPessoaNaEscala | null, texto: string) => void;
  aoErro: (texto: string) => void;
}) {
  const f = p.ferias;
  const s = selo(p, hoje);
  const atual = f?.periodos.find((x) => x.situacao === 'EM_AQUISICAO');
  return (
    <li className={`qh-ferias-linha${aberta ? ' qh-ferias-aberta' : ''}`}>
      <button type="button" className="qh-ferias-topo" onClick={aoAbrir} aria-expanded={aberta}>
        <span className="qh-ferias-nome">
          <strong>{p.nome}</strong>
          <small>{p.admissao ? `Admissão ${dataBr(p.admissao)}` : 'Sem data de admissão'}</small>
        </span>
        <span className="qh-ferias-num">
          <strong>{f ? f.saldo : '—'}</strong>
          <small>dias de saldo</small>
        </span>
        <span className="qh-ferias-num">
          <strong>{f?.proximoVencimento ? dataBr(f.proximoVencimento.limite) : '—'}</strong>
          <small>tirar até</small>
        </span>
        <span className="qh-ferias-num">
          <strong>{atual ? `${String(Math.max(0, atual.direito - atual.usados))}` : '—'}</strong>
          <small>{atual ? `completa ${diaMes(somarUmDia(atual.fim))}` : 'próximo período'}</small>
        </span>
        <span className={`qh-selo-ferias qh-selo-${s.tom}`}>{s.texto}</span>
        <span className="qh-ferias-seta" aria-hidden="true">
          <Icone nome="chev-d" tamanho={16} />
        </span>
      </button>
      {aberta && (
        <DetalheDeFerias p={p} hoje={hoje} sessao={sessao} aoMudar={aoMudar} aoErro={aoErro} />
      )}
    </li>
  );
}

const somarUmDia = (d: string) =>
  new Date(Date.parse(`${d}T12:00:00Z`) + 86_400_000).toISOString().slice(0, 10);

export function DetalheDeFerias({
  p,
  hoje,
  sessao,
  aoMudar,
  aoErro,
}: {
  p: FeriasDaPessoaNaEscala;
  hoje: string;
  sessao: Sessao;
  aoMudar: (p: FeriasDaPessoaNaEscala | null, texto: string) => void;
  aoErro: (texto: string) => void;
}) {
  const [admissao, setAdmissao] = useState(p.admissao ?? '');
  const salvarAdmissao = async () => {
    const r = await definirAdmissao(sessao, p.pessoaId, admissao || null);
    if (r.tipo === 'confirmada') aoMudar(r.dados.pessoa, `${p.nome}: admissão salva.`);
    else aoErro(r.tipo === 'recusada' ? r.mensagem : 'A internet caiu — confira.');
  };
  return (
    <div className="qh-ferias-detalhe">
      <div className="qh-linha-campos">
        <label className="qh-campo">
          Data de admissão
          <input
            type="date"
            value={admissao}
            max={hojeBrasilia()}
            onChange={(e) => setAdmissao(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="qh-btn qh-btn-sec"
          onClick={() => void salvarAdmissao()}
          disabled={(admissao || null) === p.admissao}
        >
          Salvar admissão
        </button>
      </div>
      {!p.ferias ? (
        <>
          <p className="qh-mudo">
            Sem a data de admissão não dá para saber o saldo. O que já está no quadro como férias:
          </p>
          <Blocos blocos={p.tiradas} hoje={hoje} />
        </>
      ) : (
        <div className="qh-periodos">
          {[...p.ferias.periodos].reverse().map((x) => (
            <Periodo
              key={x.inicio}
              pessoa={p}
              x={x}
              hoje={hoje}
              sessao={sessao}
              aoMudar={aoMudar}
              aoErro={aoErro}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Blocos({ blocos, hoje }: { blocos: readonly BlocoDeFerias[]; hoje: string }) {
  if (blocos.length === 0) return <p className="qh-mudo">Nenhum dia de férias no quadro.</p>;
  return (
    <ul className="qh-blocos">
      {blocos.map((b) => (
        <li key={b.de} className={b.de > hoje ? 'qh-bloco-futuro' : undefined}>
          <span className="qh-amostra qh-amostra-fe">FE</span>
          {textoDoBloco(b)}
          {b.de > hoje && <small> · marcada</small>}
        </li>
      ))}
    </ul>
  );
}

function Periodo({
  pessoa,
  x,
  hoje,
  sessao,
  aoMudar,
  aoErro,
}: {
  pessoa: FeriasDaPessoaNaEscala;
  x: PeriodoDeFerias;
  hoje: string;
  sessao: Sessao;
  aoMudar: (p: FeriasDaPessoaNaEscala | null, texto: string) => void;
  aoErro: (texto: string) => void;
}) {
  const [dias, setDias] = useState('');
  const [motivo, setMotivo] = useState('');
  const pct = x.direito ? Math.min(100, Math.round((x.usados / x.direito) * 100)) : 100;
  const incluir = async (d: number, m: string) => {
    const r = await incluirAjusteDeFerias(sessao, {
      pessoaId: pessoa.pessoaId,
      periodo: x.inicio,
      dias: d,
      motivo: m,
    });
    if (r.tipo === 'confirmada') {
      setDias('');
      setMotivo('');
      aoMudar(r.dados.pessoa, `${pessoa.nome}: ajuste salvo.`);
    } else aoErro(r.tipo === 'recusada' ? r.mensagem : 'A internet caiu — confira.');
  };
  const remover = async (id: string) => {
    if (!window.confirm('Desfazer este ajuste?')) return;
    const r = await removerAjusteDeFerias(sessao, id);
    if (r.tipo === 'confirmada') aoMudar(null, 'Ajuste desfeito.');
    else aoErro(r.tipo === 'recusada' ? r.mensagem : 'A internet caiu — confira.');
  };
  return (
    <section className={`qh-periodo-ferias qh-periodo-${x.situacao.toLowerCase()}`}>
      <header>
        <div>
          <strong>
            Período {dataBr(x.inicio)} a {dataBr(x.fim)}
          </strong>
          <small>
            {x.situacao === 'EM_AQUISICAO'
              ? `Completa em ${dataBr(somarUmDia(x.fim))}`
              : `Tirar até ${dataBr(x.limite)}${x.situacao !== 'QUITADO' && x.limite >= hoje ? ` (faltam ${String(diasAte(x.limite, hoje))} dias)` : ''}`}
          </small>
        </div>
        <span
          className={`qh-selo-ferias qh-selo-${x.situacao === 'VENCIDO' ? 'erro' : x.situacao === 'VENCENDO' ? 'alerta' : x.situacao === 'QUITADO' ? 'neutro' : 'ok'}`}
        >
          {ROTULO_DA_SITUACAO[x.situacao]}
        </span>
      </header>
      <div className="qh-ferias-numeros">
        <span>
          Direito <strong>{x.direito}</strong>
          {x.faltas > 5 && <small> ({x.faltas} faltas)</small>}
        </span>
        <span>
          Usou <strong>{x.usados}</strong>
        </span>
        <span>
          Saldo <strong className={x.saldo < 0 ? 'qh-n-ruim' : ''}>{x.saldo}</strong>
        </span>
      </div>
      <div
        className="qh-barra-ferias"
        role="img"
        aria-label={`Usou ${String(x.usados)} de ${String(x.direito)} dias`}
      >
        <span style={{ width: `${String(pct)}%` }} />
      </div>
      <Blocos blocos={x.blocos} hoje={hoje} />
      {x.avisoFracionamento && <p className="qh-aviso-fraco">⚠ {x.avisoFracionamento}</p>}
      {x.ajustes.length > 0 && (
        <ul className="qh-ajustes">
          {x.ajustes.map((a) => (
            <li key={a.id}>
              <span>
                {a.dias > 0 ? `−${String(a.dias)}` : `+${String(-a.dias)}`} dias · {a.motivo}
              </span>
              <button
                type="button"
                className="qh-btn-icone"
                onClick={() => void remover(a.id)}
                aria-label="Desfazer ajuste"
              >
                <Icone nome="x" tamanho={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <details className="qh-ajuste-novo">
        <summary>Lançar ajuste (antes do sistema, venda de dias…)</summary>
        <div className="qh-atalhos">
          <button
            type="button"
            className="qh-btn qh-btn-sec qh-btn-curto"
            onClick={() => void incluir(10, 'Vendeu 10 dias (abono)')}
          >
            Vendeu 10 dias
          </button>
        </div>
        <div className="qh-linha-campos">
          <label className="qh-campo">
            Dias que já usou
            <input
              type="number"
              min={-60}
              max={60}
              value={dias}
              onChange={(e) => setDias(e.target.value)}
              placeholder="ex.: 10"
            />
          </label>
          <label className="qh-campo">
            Motivo
            <input
              value={motivo}
              maxLength={120}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="ex.: tirou antes do sistema (jan/2026)"
            />
          </label>
          <button
            type="button"
            className="qh-btn qh-btn-pri"
            disabled={!Number(dias) || motivo.trim().length < 2}
            onClick={() => void incluir(Number(dias), motivo.trim())}
          >
            Lançar
          </button>
        </div>
      </details>
    </section>
  );
}

/** Na "Minha escala": o meu saldo e o que já tirei. */
export function MinhasFerias({ f }: { f: FeriasDaPessoaNaEscala }) {
  const hoje = hojeBrasilia();
  const tiradas = f.ferias ? f.ferias.periodos.flatMap((p) => p.blocos) : f.tiradas;
  const ultimas = tiradas
    .filter((b) => b.de <= hoje)
    .slice(-4)
    .reverse();
  const marcadas = f.ferias?.marcadas ?? f.tiradas.filter((b) => b.de > hoje);
  return (
    <section className="qh-minhas-ferias">
      <h2>Minhas férias</h2>
      {f.ferias ? (
        <div className="qh-kpis">
          <Kpi
            rotulo="Saldo de férias"
            valor={`${String(f.ferias.saldo)} dias`}
            destaque={f.ferias.saldo > 0}
          />
          <Kpi
            rotulo="Tirar até"
            valor={f.ferias.proximoVencimento ? dataBr(f.ferias.proximoVencimento.limite) : '—'}
          />
        </div>
      ) : (
        <p className="qh-mudo">
          O Carlos ainda não pôs sua data de admissão — o saldo aparece depois.
        </p>
      )}
      {marcadas.length > 0 && (
        <p className="qh-previa">
          <strong>Marcadas:</strong> {marcadas.map(textoDoBloco).join(' · ')}
        </p>
      )}
      {ultimas.length > 0 && (
        <p className="qh-mudo">
          <strong>Já tirou:</strong> {ultimas.map(textoDoBloco).join(' · ')}
        </p>
      )}
    </section>
  );
}
